import { Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { formatCurrencyDollars } from 'helpers/formatters';

import { CardLink } from '../PropertyCard';
import { buildAddressLines, buildPropertySpecsLine } from './helpers';
import type { ListingCardData } from './types';

export const ListingCardPricingDetails = ({
  listing,
  href,
  openInNewTab,
}: {
  listing: ListingCardData;
  href: string;
  openInNewTab: boolean;
}) => {
  const address = listing.street || buildAddressLines(listing).primaryLine || 'View home';
  const hasPrice = listing.listPrice != null;
  return (
    <Col className="min-w-0 gap-1">
      <CardLink
        href={href}
        external={openInNewTab}
        ariaLabel={`Property details: ${address}`}
        analyticsName="marketplace-property-card-info-link"
      >
        <Text as="span" className="text-label-md text-od-content-primary">
          {address}
        </Text>
      </CardLink>
      <Text className="text-body-sm text-od-content-primary">
        {hasPrice ? formatCurrencyDollars(listing.listPrice) : 'Price pending'}
        {hasPrice && listing.estimatedMonthlyPayment != null && (
          <>
            {' · '}
            <Text
              as="span"
              className="whitespace-nowrap text-body-sm"
              title="Estimated monthly principal and interest with 20% down over 30 years. Taxes, insurance, and fees are excluded."
            >
              {formatCurrencyDollars(Math.round(listing.estimatedMonthlyPayment))} / mo
            </Text>
          </>
        )}
        {hasPrice && listing.estimatedAnnualInterestRatePercent != null && (
          <>
            {' · '}
            <Text
              as="span"
              className="whitespace-nowrap text-body-sm"
              title="Interest rate assumed for the payment estimate, not a personalized loan offer."
            >
              {listing.estimatedAnnualInterestRatePercent.toLocaleString('en-US', {
                maximumFractionDigits: 2,
              })}
              % rate
            </Text>
          </>
        )}
      </Text>
      <Text className="text-body-sm text-od-content-secondary">
        {buildPropertySpecsLine(listing.beds, listing.baths, listing.livingSquareFootage)}
      </Text>
    </Col>
  );
};
